import React, { useEffect, useRef, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFileArrowUp, faFolderOpen, faMinus, faRotate } from "@fortawesome/free-solid-svg-icons";
import { Button, Form, Modal, Spinner, Table } from "react-bootstrap";
import { toast } from "react-toastify";
import MyModal from "../Common/MyModal";
import MyPagination from "../Common/MyPagination";
import {
  AssetImportOperation,
  AssetImportContactMatch,
  AssetImportSession,
  AssetImportSessionRow,
  useCreateAssetImportSessionMutation,
  useDeleteAssetImportSessionErrorRowsMutation,
  useGetAssetImportSessionsQuery,
  useGetAssetImportSessionQuery,
  useGetAssetImportSessionRowsQuery,
  useGetWorkflowFilesQuery,
  useRunAssetImportSessionMutation,
  useUpdateAssetImportSessionRowExclusionsMutation,
  useUploadWorkflowFileMutation,
} from "../store/slices/mapsSlice";

type ImportAssetsModalProps = {
  show: boolean;
  onHide: () => void;
};

const fixedFields = [
  "Reference",
  "Label",
  "Type",
  "Description",
  "Address",
  "Contacts",
  "ParentReferences",
  "ChildReferences",
  "GeoJSON",
  "LatLng",
  "LngLat",
];

const emptyCsvTemplateFields = [
  "Reference",
  "Label",
  "Type",
  "Description",
  "Address",
  "Contacts",
  "ParentReferences",
  "ChildReferences",
  "LatLng",
];

const emptyCsvTemplateHref = `data:text/csv;charset=utf-8,${encodeURIComponent(
  `${emptyCsvTemplateFields.join(",")}\n`
)}`;

const activeStatuses = ["analysing", "queued", "processing"];

const formatFileSize = (bytes?: number) => {
  if (!bytes) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${Math.round((bytes / (1024 * 1024)) * 10) / 10} MB`;
};

const formatUploadedAt = (value?: string) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString();
};

const rowValue = (row: AssetImportSessionRow, field: string) => {
  const normalized = row.normalized || {};
  const raw = row.raw || {};
  return normalized[field] ?? raw[field] ?? raw[field.toLowerCase()] ?? "";
};

const rowErrors = (row: AssetImportSessionRow) => {
  if (Array.isArray(row.errors)) return row.errors;
  return [];
};

const importRowActionLabel = (row: AssetImportSessionRow) => {
  if (["error", "excluded", "duplicate", "ignored"].includes(row.status)) return "-";
  return row.action || "-";
};

const importRowStatusBadgeClass = (status: AssetImportSessionRow["status"]) => {
  switch (status) {
    case "valid":
    case "imported":
      return "badge text-bg-success text-capitalize";
    case "ignored":
      return "badge text-bg-secondary text-capitalize";
    case "excluded":
      return "badge text-bg-light text-dark border text-capitalize";
    case "duplicate":
      return "badge text-bg-warning text-capitalize";
    case "error":
    default:
      return "badge text-bg-danger text-capitalize";
  }
};

const importRowIgnoredReason = (row: AssetImportSessionRow) => {
  if (row.status !== "ignored") return "";
  const message = rowErrors(row).join(" ").toLowerCase();
  if (message.includes("ignored: update")) return "update";
  if (message.includes("ignored: create")) return "create";
  return "error";
};

const hasRunStarted = (session: AssetImportSession) =>
  ["queued", "processing", "completed", "failed"].includes(session.status) ||
  session.rows_imported > 0 ||
  session.failed_rows_during_run > 0 ||
  session.contacts_created > 0;

const formatContactValue = (value: unknown) => {
  if (Array.isArray(value)) return value.join(", ");
  if (value === undefined || value === null) return "";
  return String(value);
};

const normalizeContactMatches = (row: AssetImportSessionRow): AssetImportContactMatch[] => {
  let matches = row.contact_matches as any;
  if (typeof matches === "string") {
    try {
      matches = JSON.parse(matches);
    } catch (_error) {
      matches = [];
    }
  }
  if (!Array.isArray(matches)) return [];

  return matches
    .map((match) => ({
      value: match.value ?? match.Value ?? "",
      channel: match.channel ?? match.Channel,
      valid: match.valid ?? match.Valid ?? false,
      matched: match.matched ?? match.Matched ?? false,
      message: match.message ?? match.Message,
    }))
    .filter((match) => !!match.value);
};

const renderContactMatch = (match: AssetImportContactMatch) => {
  const className = match.matched
    ? "badge rounded-pill text-bg-success"
    : match.valid
    ? "badge rounded-pill text-bg-warning"
    : "badge rounded-pill text-bg-danger";
  const label = match.matched ? "Matched" : match.valid ? "Missing" : "Invalid";
  const title = match.message || label;

  return (
    <span key={`${match.value}-${match.channel || "contact"}`} className={className} title={title}>
      {match.value}
      <span className="ms-1 opacity-75">{label}</span>
    </span>
  );
};

const renderContactsCell = (row: AssetImportSessionRow) => {
  const contactMatches = normalizeContactMatches(row);
  if (contactMatches.length) {
    return <div className="d-flex flex-wrap gap-1">{contactMatches.map(renderContactMatch)}</div>;
  }

  const contacts = formatContactValue(rowValue(row, "Contacts"));
  return contacts || <span className="text-muted">-</span>;
};

const sessionActive = (session?: AssetImportSession) => !!session && activeStatuses.includes(session.status);
const sessionCanContinue = (session?: Pick<AssetImportSession, "status" | "rows_valid"> & { has_rows?: boolean }) =>
  !!session && session.status === "ready" && session.rows_valid > 0 && session.has_rows !== false;

export default function ImportAssetsModal({ show, onHide }: ImportAssetsModalProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [operation, setOperation] = useState<AssetImportOperation>("upsert");
  const [skipEmptyUpdate, setSkipEmptyUpdate] = useState(true);
  const [createContactsIfNotFound, setCreateContactsIfNotFound] = useState(true);
  const [keepFileInLibrary, setKeepFileInLibrary] = useState(false);
  const [selectedLibraryFile, setSelectedLibraryFile] = useState("");
  const [showLibrary, setShowLibrary] = useState(false);
  const [librarySearch, setLibrarySearch] = useState("");
  const [libraryPage, setLibraryPage] = useState(0);
  const [libraryPerPage, setLibraryPerPage] = useState(10);
  const [isDraggingFile, setIsDraggingFile] = useState(false);
  const [sourceFilename, setSourceFilename] = useState("");
  const [sessionUuid, setSessionUuid] = useState("");
  const [localSession, setLocalSession] = useState<AssetImportSession | null>(null);
  const [previewPage, setPreviewPage] = useState(0);
  const [previewPerPage, setPreviewPerPage] = useState(10);
  const [showOnlyErrorRows, setShowOnlyErrorRows] = useState(false);

  const [createAssetImportSession, createSessionState] = useCreateAssetImportSessionMutation();
  const [runAssetImportSession, runSessionState] = useRunAssetImportSessionMutation();
  const [updateRowExclusions, updateExclusionsState] = useUpdateAssetImportSessionRowExclusionsMutation();
  const [deleteErrorRows, deleteErrorRowsState] = useDeleteAssetImportSessionErrorRowsMutation();
  const [uploadWorkflowFile, uploadState] = useUploadWorkflowFileMutation();

  const { data: polledSession, refetch: refetchSession } = useGetAssetImportSessionQuery(sessionUuid, {
    skip: !sessionUuid,
    pollingInterval: sessionActive(localSession || undefined) ? 3000 : 0,
  });
  const session = polledSession || localSession;
  const isImporting = sessionActive(session);
  const { data: previewRows, isFetching: isFetchingRows, refetch: refetchRows } = useGetAssetImportSessionRowsQuery(
    { uuid: sessionUuid, limit: previewPerPage, offset: previewPage * previewPerPage, status: showOnlyErrorRows ? "error" : undefined },
    { skip: !sessionUuid }
  );
  const { data: workflowFiles, isFetching: isFetchingFiles, refetch: refetchWorkflowFiles } = useGetWorkflowFilesQuery(
    { page: libraryPage + 1, perPage: libraryPerPage, search: librarySearch, extensions: ["csv", "json"] },
    { skip: !show }
  );
  const { data: recentSessions, refetch: refetchRecentSessions } = useGetAssetImportSessionsQuery(
    { limit: 6 },
    { skip: !show }
  );

  useEffect(() => {
    if (polledSession) {
      setLocalSession(polledSession);
      if (["completed", "failed"].includes(polledSession.status)) {
        refetchRows();
        refetchWorkflowFiles();
      }
    }
  }, [polledSession, refetchRows, refetchWorkflowFiles]);

  useEffect(() => {
    if (!show) {
      if (fileInputRef.current) fileInputRef.current.value = "";
      setKeepFileInLibrary(false);
      setSelectedLibraryFile("");
      setShowLibrary(false);
      setLibrarySearch("");
      setLibraryPage(0);
      setLibraryPerPage(10);
      setIsDraggingFile(false);
      setSourceFilename("");
      setOperation("upsert");
      setSkipEmptyUpdate(true);
      setCreateContactsIfNotFound(true);
      setSessionUuid("");
      setLocalSession(null);
      setPreviewPage(0);
      setPreviewPerPage(10);
      setShowOnlyErrorRows(false);
    }
  }, [show]);

  useEffect(() => {
    setLibraryPage(0);
  }, [librarySearch]);

  const isWorking =
    createSessionState.isLoading ||
    runSessionState.isLoading ||
    uploadState.isLoading ||
    updateExclusionsState.isLoading ||
    deleteErrorRowsState.isLoading ||
    isImporting;
  const storageChoiceLocked = !!sessionUuid || !!sourceFilename || !!selectedLibraryFile;
  const hasSelectedSource = !!sourceFilename || !!sessionUuid || !!selectedLibraryFile;
  const previewMatchesOptions =
    !session ||
    (session.operation === operation &&
      session.skip_empty_update === skipEmptyUpdate &&
      session.create_contacts_if_not_found === createContactsIfNotFound);
  const canRefreshPreview = !!session?.workflow_file_uuid && !isWorking;

  const createSession = async (body: any, filename?: string) => {
    const response: any = await createAssetImportSession({
      operation,
      skipEmptyUpdate,
      createContactsIfNotFound,
      ...body,
    });
    if (response.error) {
      toast.error("Asset import preview failed");
      return;
    }
    setLocalSession(response.data);
    setSessionUuid(response.data.uuid);
    setPreviewPage(0);
    if (filename) setSourceFilename(filename);
    toast.success("Import preview ready");
  };

  const refreshPreviewWithCurrentOptions = async () => {
    if (!session?.workflow_file_uuid) {
      toast.error("Select a file before updating the preview");
      return;
    }

    await createSession(
      { source: session.source === "library" ? "library" : "upload", workflowFileUuid: session.workflow_file_uuid },
      sourceFilename || session.filename
    );
  };

  const handleFile = async (file?: File) => {
    if (!file) return;
    setSelectedLibraryFile("");
    setSourceFilename(file.name);
    setLocalSession(null);
    setSessionUuid("");

    const response: any = await uploadWorkflowFile({ file, temporary: !keepFileInLibrary });
    if (response.error) {
      toast.error(keepFileInLibrary ? "File could not be saved to the documents library" : "File could not be prepared for import");
      return;
    }
    const workflowFileUuid = response.data?.uuid || response.data?.Uuid || response.data?.UUID;
    if (!workflowFileUuid) {
      toast.error("Uploaded file could not be prepared for import");
      return;
    }
    await createSession({ source: keepFileInLibrary ? "library" : "upload", workflowFileUuid }, file.name);
    if (keepFileInLibrary) {
      refetchWorkflowFiles();
    }
  };

  const handleFileInput = (file?: File) => {
    handleFile(file);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleLibraryFile = async (uuid: string) => {
    const file = workflowFiles?.files.find((item) => item.uuid === uuid);
    setSelectedLibraryFile(uuid);
    setKeepFileInLibrary(true);
    setShowLibrary(false);
    if (sessionCanContinue(file?.asset_import as any)) {
      setLocalSession({
        uuid: file?.asset_import?.session_uuid || "",
        created_at: file?.asset_import?.updated_at || "",
        updated_at: file?.asset_import?.updated_at || "",
        workflow_file_uuid: uuid,
        source: "library",
        status: file?.asset_import?.status || "ready",
        operation,
        filename: file?.filename || "library-file",
        content_type: file?.content_type || "",
        size_bytes: file?.size_bytes || 0,
        skip_empty_update: skipEmptyUpdate,
        create_contacts_if_not_found: createContactsIfNotFound,
        rows_received: file?.asset_import?.rows_received || 0,
        rows_valid: file?.asset_import?.rows_valid || 0,
        rows_with_errors: file?.asset_import?.rows_with_errors || 0,
        rows_duplicate: file?.asset_import?.rows_duplicate || 0,
        rows_excluded: file?.asset_import?.rows_excluded || 0,
        rows_ignored: 0,
        rows_imported: file?.asset_import?.rows_imported || 0,
        assets_to_create: 0,
        assets_to_update: 0,
        contacts_matched: 0,
        contacts_missing: 0,
        contacts_created: 0,
        relations_added: 0,
        locations_set: 0,
        failed_rows_during_run: 0,
        error_message: file?.asset_import?.error_message,
      });
      setSessionUuid(file?.asset_import?.session_uuid || "");
      setSourceFilename(file?.filename || "library-file");
      setPreviewPage(0);
      toast.success("Import preview ready");
      return;
    }
    await createSession({ source: "library", workflowFileUuid: uuid }, file?.filename || "library-file");
  };

  const continueSession = (recentSession: AssetImportSession) => {
    setLocalSession(recentSession);
    setSessionUuid(recentSession.uuid);
    setSourceFilename(recentSession.filename || "Inline rows");
    setSelectedLibraryFile(recentSession.workflow_file_uuid || "");
    setKeepFileInLibrary(recentSession.source === "library");
    setOperation(recentSession.operation);
    setSkipEmptyUpdate(recentSession.skip_empty_update);
    setCreateContactsIfNotFound(recentSession.create_contacts_if_not_found);
    setPreviewPage(0);
  };

  const handleRun = async () => {
    if (!sessionUuid || session?.status !== "ready") {
      toast.error("Preview a ready import session before running import");
      return;
    }
    if (!previewMatchesOptions) {
      toast.error("Update the preview before running import");
      return;
    }
    const response: any = await runAssetImportSession(sessionUuid);
    if (response.error) {
      toast.error("Asset import could not be queued");
      return;
    }
    setLocalSession(response.data);
    refetchRecentSessions();
    toast.success("Asset import queued");
  };

  const toggleRowExcluded = async (row: AssetImportSessionRow) => {
    if (!sessionUuid) return;
    if (["error", "duplicate"].includes(row.status)) {
      const response: any = await deleteErrorRows({ uuid: sessionUuid, rowNumbers: [row.row_number] });
      if (response.error) {
        toast.error("Row could not be removed");
        return;
      }
      setLocalSession(response.data);
      setPreviewPage(0);
      toast.success("Row removed");
      return;
    }
    const excluded = row.status !== "excluded";
    const response: any = await updateRowExclusions({ uuid: sessionUuid, rowNumbers: [row.row_number], excluded });
    if (response.error) {
      toast.error("Row could not be updated");
      return;
    }
    setLocalSession(response.data);
    refetchRows();
  };

  const removeRowsWithErrors = async () => {
    if (!sessionUuid || !session || session.rows_with_errors === 0) return;
    const response: any = await deleteErrorRows({ uuid: sessionUuid });
    if (response.error) {
      toast.error("Rows with errors could not be removed");
      return;
    }
    setLocalSession(response.data);
    setPreviewPage(0);
    toast.success("Rows with errors removed");
  };

  const clearSession = () => {
    if (fileInputRef.current) fileInputRef.current.value = "";
    setSessionUuid("");
    setLocalSession(null);
    setSourceFilename("");
    setSelectedLibraryFile("");
    setKeepFileInLibrary(false);
    setShowLibrary(false);
    setLibrarySearch("");
    setLibraryPage(0);
    setIsDraggingFile(false);
    setPreviewPage(0);
    setShowOnlyErrorRows(false);
  };

  return (
    <MyModal label={null} heading="Import assets" show={show} onHide={onHide} size="xl" centered>
      <>
        <div className="row g-3 mb-3">
          <div className="col-lg-5">
            <section className="border rounded p-3 h-100">
              <div className="fw-bold mb-2">Source file</div>
              {!hasSelectedSource && (
                <div className="row g-2">
                  <div className="col-12 col-md-6">
                    <button
                      type="button"
                      className={`btn w-100 border d-flex flex-column align-items-center justify-content-center gap-2 py-3 h-100 ${
                        isDraggingFile ? "btn-primary" : "btn-light"
                      }`}
                      disabled={isWorking}
                      onClick={() => fileInputRef.current?.click()}
                      onDragOver={(event) => {
                        event.preventDefault();
                        setIsDraggingFile(true);
                      }}
                      onDragLeave={() => setIsDraggingFile(false)}
                      onDrop={(event) => {
                        event.preventDefault();
                        setIsDraggingFile(false);
                        handleFileInput(event.dataTransfer.files?.[0]);
                      }}
                    >
                      <FontAwesomeIcon icon={faFileArrowUp} size="lg" />
                      <span>Upload new file</span>
                      <span className="small text-muted">CSV or JSON</span>
                    </button>
                    <input
                      ref={fileInputRef}
                      type="file"
                      className="d-none"
                      accept=".csv,.json,application/json,text/csv"
                      onChange={(event) => handleFileInput(event.currentTarget.files?.[0])}
                      disabled={isWorking}
                    />
                  </div>
                  <div className="col-12 col-md-6">
                    <button
                      type="button"
                      className="btn btn-light w-100 border d-flex flex-column align-items-center justify-content-center gap-2 py-3 h-100"
                      disabled={isWorking}
                      onClick={() => setShowLibrary(true)}
                    >
                      <FontAwesomeIcon icon={faFolderOpen} size="lg" />
                      <span>Select from library</span>
                      <span className="small text-muted">Documents library</span>
                    </button>
                  </div>
                </div>
              )}
              <Form.Check
                className="mt-3"
                type="checkbox"
                label="Keep the file in the documents library"
                checked={keepFileInLibrary}
                onChange={(event) => {
                  if (!storageChoiceLocked) setKeepFileInLibrary(event.target.checked);
                }}
                disabled={isWorking || storageChoiceLocked}
              />
              <div className="small text-muted mt-1">
                Unchecked files are prepared for this import only and removed after import or inactivity.
                {storageChoiceLocked && " This choice is locked until the selected file is removed."}
              </div>
              <ul className="list-group list-group-flush mt-2">
                <li className="list-group-item px-0 py-1 d-flex align-items-center justify-content-between">
                  <span className="text-truncate small text-muted">{sourceFilename || "No file selected"}</span>
                  {sourceFilename && (
                    <button
                      type="button"
                      className="btn btn-outline-danger btn-sm border-0"
                      title="Remove file"
                      onClick={clearSession}
                      disabled={isWorking}
                    >
                      <FontAwesomeIcon icon={faMinus} />
                    </button>
                  )}
                </li>
              </ul>
            </section>
          </div>

          <div className="col-lg-4">
            <section className="border rounded p-3 h-100">
              <div className="fw-bold mb-2">Import options</div>
              <Form.Group className="mb-3">
                <Form.Label className="fw-bold">Operation</Form.Label>
                <Form.Select
                  value={operation}
                  onChange={(event) => setOperation(event.target.value as AssetImportOperation)}
                  disabled={isWorking}
                >
                  <option value="create">Create only</option>
                  <option value="update">Update only</option>
                  <option value="upsert">Create or Update</option>
                </Form.Select>
              </Form.Group>
              <Form.Check
                type="checkbox"
                label="Skip empty updates"
                checked={skipEmptyUpdate}
                onChange={(event) => setSkipEmptyUpdate(event.target.checked)}
                disabled={isWorking}
              />
              <div className="small text-muted mt-1 mb-3">
                Empty optional fields in update rows leave the existing asset value unchanged.
              </div>
              <Form.Check
                type="checkbox"
                label="Create contacts"
                checked={createContactsIfNotFound}
                onChange={(event) => setCreateContactsIfNotFound(event.target.checked)}
                disabled={isWorking}
              />
              <div className="small text-muted mt-1">
                Contact values in the Contacts field are created when no matching contact exists, then linked to the asset.
              </div>
              {session && !previewMatchesOptions && (
                <div className="border-top mt-3 pt-3">
                  <div className="small text-warning-emphasis mb-2">Preview uses previous import options.</div>
                  <Button variant="outline-primary" size="sm" onClick={refreshPreviewWithCurrentOptions} disabled={!canRefreshPreview}>
                    {createSessionState.isLoading && <Spinner animation="border" size="sm" className="me-2" />}
                    Update preview
                  </Button>
                </div>
              )}
            </section>
          </div>

          <div className="col-lg-3">
            <section className="border rounded p-3 h-100">
              <div className="fw-bold mb-2">Fixed fields</div>
              <div className="small text-muted mb-3">{fixedFields.join(", ")}</div>
              <a className="btn btn-outline-secondary btn-sm" href={emptyCsvTemplateHref} download="asset-import-template.csv">
                Download empty CSV
              </a>
            </section>
          </div>
        </div>

        {session && (
          <section className="border rounded p-3 mb-3">
            <div className="d-flex align-items-start justify-content-between gap-3 mb-3">
              <div>
                <div className="fw-bold">Import session</div>
                <div className="small text-muted">
                  Status: <span className="text-capitalize">{session.status}</span>
                  {isImporting && <Spinner animation="border" size="sm" className="ms-2" />}
                </div>
                {session.error_message && <div className="text-danger small mt-1">{session.error_message}</div>}
              </div>
              <div className="d-flex gap-2">
                {hasRunStarted(session) && (
                  <Button
                    variant="outline-secondary"
                    size="sm"
                    disabled={!sessionUuid || isWorking}
                    onClick={() => refetchSession()}
                    title="Reload the latest status and results for this import run"
                  >
                    <FontAwesomeIcon icon={faRotate} className="me-2" />
                    Refresh import status
                  </Button>
                )}
                <Button variant="primary" onClick={handleRun} disabled={isWorking || !previewMatchesOptions || session.status !== "ready" || session.rows_valid === 0}>
                  {runSessionState.isLoading && <Spinner animation="border" size="sm" className="me-2" />}
                  Run import
                </Button>
              </div>
            </div>
            <div className="row g-2 small">
              <div className="col-6 col-md-2">Rows: {session.rows_received}</div>
              <div className="col-6 col-md-2">Valid: {session.rows_valid}</div>
              <div className="col-6 col-md-2">Errors: {session.rows_with_errors}</div>
              <div className="col-6 col-md-2">Duplicates: {session.rows_duplicate}</div>
              <div className="col-6 col-md-2">Ignored: {session.rows_ignored}</div>
              <div className="col-6 col-md-2">Excluded: {session.rows_excluded}</div>
              <div className="col-6 col-md-2">Creates: {session.assets_to_create}</div>
              <div className="col-6 col-md-2">Updates: {session.assets_to_update}</div>
              <div className="col-6 col-md-2">Contacts matched: {session.contacts_matched}</div>
              <div className="col-6 col-md-2">Contacts missing: {session.contacts_missing}</div>
            </div>
            {hasRunStarted(session) && (
              <div className="border-top mt-3 pt-3">
                <div className="fw-bold mb-2">Run results</div>
                <div className="row g-2 small">
                  <div className="col-6 col-md-2">Imported: {session.rows_imported}</div>
                  <div className="col-6 col-md-2">Contacts created: {session.contacts_created}</div>
                  <div className="col-6 col-md-2">Run failures: {session.failed_rows_during_run}</div>
                </div>
              </div>
            )}
          </section>
        )}

        <section className="border rounded p-3">
          <div className="d-flex align-items-center justify-content-between gap-2 mb-3">
            <div>
              <div className="fw-bold">Preview</div>
              <div className="small text-muted">
                {session ? `${previewRows?.total || 0} rows available` : "Upload or select a file to create a preview"}
              </div>
            </div>
            <div className="d-flex align-items-center gap-2 flex-wrap justify-content-end">
              {session && (
                <Form.Check
                  type="checkbox"
                  id="asset-import-errors-only"
                  label="Show only rows with errors"
                  checked={showOnlyErrorRows}
                  onChange={(event) => {
                    setShowOnlyErrorRows(event.target.checked);
                    setPreviewPage(0);
                  }}
                  disabled={isFetchingRows}
                />
              )}
              {session && (
                <Button
                  variant="outline-danger"
                  size="sm"
                  onClick={removeRowsWithErrors}
                  disabled={isWorking || session.status !== "ready" || session.rows_with_errors === 0}
                >
                  Remove rows with errors
                </Button>
              )}
              {isFetchingRows && <Spinner animation="border" size="sm" />}
            </div>
          </div>

          {session ? (
            <>
              <div className="table-responsive">
                <Table size="sm" className="align-middle mb-0">
                  <thead>
                    <tr>
                      <th>Row</th>
                      <th>Reference</th>
                      <th>Action</th>
                      <th>Status</th>
                      <th>Label</th>
                      <th>Type</th>
                      <th>Contacts</th>
                      <th>Errors</th>
                      <th className="text-end">Import</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(previewRows?.rows || []).map((row) => {
                      const errors = rowErrors(row);
                      const canExclude = session.status === "ready" && ["valid", "excluded", "error", "duplicate"].includes(row.status);
                      const isRemoved = row.status === "excluded";
                      const isIgnored = row.status === "ignored";
                      const ignoredReason = importRowIgnoredReason(row);
                      return (
                        <tr key={row.row_number} className={isRemoved || isIgnored ? "text-muted" : undefined}>
                          <td>{row.row_number}</td>
                          <td>{row.reference}</td>
                          <td className="text-capitalize">{importRowActionLabel(row)}</td>
                          <td>
                            <span className={importRowStatusBadgeClass(row.status)}>{row.status}</span>
                            {ignoredReason && <span className="badge text-bg-light text-dark border ms-1">{ignoredReason}</span>}
                          </td>
                          <td>{rowValue(row, "Label") || <span className="text-muted">-</span>}</td>
                          <td>{rowValue(row, "Type") || <span className="text-muted">-</span>}</td>
                          <td>{renderContactsCell(row)}</td>
                          <td>{errors.length > 0 ? errors.join(", ") : <span className="text-muted">-</span>}</td>
                          <td className="text-end">
                            <button
                              type="button"
                              className={`btn btn-sm ${row.status === "excluded" ? "btn-outline-primary" : "btn-outline-danger"}`}
                              disabled={!canExclude || updateExclusionsState.isLoading}
                              onClick={() => toggleRowExcluded(row)}
                            >
                              {row.status === "excluded" ? "Restore" : "Remove"}
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                    {(previewRows?.rows || []).length === 0 && (
                      <tr>
                        <td colSpan={9} className="text-center text-muted py-4">
                          {isFetchingRows ? "Loading rows" : "No preview rows"}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </Table>
              </div>
              <MyPagination
                total={previewRows?.total || 0}
                defaultPerPage={10}
                perPageOptions={[10, 25, 100]}
                forceRefresh={`${sessionUuid}-${session.updated_at}`}
                onPaginate={(page, perPage) => {
                  setPreviewPage(page);
                  setPreviewPerPage(perPage);
                }}
              />
            </>
          ) : (
            <div className="text-muted py-4 text-center">No file selected.</div>
          )}
        </section>

        <section className="border rounded p-3 mt-3">
          <div className="d-flex align-items-center justify-content-between gap-2 mb-3">
            <div>
              <div className="fw-bold">Recent import sessions</div>
              <div className="small text-muted">Latest asset import activity for this client.</div>
            </div>
            <button
              type="button"
              className="btn btn-link btn-sm"
              onClick={() => refetchRecentSessions()}
              title="Reload the recent asset import sessions list"
            >
              <FontAwesomeIcon icon={faRotate} className="me-2" />
              Refresh recent sessions
            </button>
          </div>
          <div className="table-responsive">
            <Table size="sm" className="align-middle mb-0">
              <thead>
                <tr>
                  <th>File</th>
                  <th>Status</th>
                  <th>Rows</th>
                  <th>Imported</th>
                  <th>Updated</th>
                  <th className="text-end">Action</th>
                </tr>
              </thead>
              <tbody>
                {(recentSessions?.sessions || []).map((recentSession, index) => (
                  <tr key={recentSession.uuid}>
                    <td>
                      <div>{recentSession.filename || "Inline rows"}</div>
                      {recentSession.rows_cleaned && (
                        <div className="small text-muted">Preview rows cleaned up</div>
                      )}
                    </td>
                    <td className="text-capitalize">{recentSession.status}</td>
                    <td>{recentSession.rows_received}</td>
                    <td>{recentSession.rows_imported}</td>
                    <td>{recentSession.updated_at ? new Date(recentSession.updated_at).toLocaleString() : "-"}</td>
                    <td className="text-end">
                      {index === 0 && sessionCanContinue(recentSession) ? (
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-primary"
                          disabled={isWorking}
                          onClick={() => continueSession(recentSession)}
                        >
                          Continue
                        </button>
                      ) : (
                        <span className="text-muted">-</span>
                      )}
                    </td>
                  </tr>
                ))}
                {(recentSessions?.sessions || []).length === 0 && (
                  <tr>
                    <td colSpan={6} className="text-center text-muted py-4">
                      No recent import sessions.
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          </div>
        </section>

        <Modal show={showLibrary} onHide={() => setShowLibrary(false)} size="xl">
          <Modal.Header closeButton>
            <Modal.Title>Select file from library</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <div style={{ minHeight: 620 }}>
              <div className="bg-colored-reverse p-3 rounded-3 mb-3">
                <div className="row g-2 align-items-end">
                  <div className="col-12 col-md-6">
                    <label className="form-label">Search</label>
                    <input
                      type="search"
                      className="form-control"
                      value={librarySearch}
                      onChange={(event) => setLibrarySearch(event.target.value)}
                      disabled={isFetchingFiles || isWorking}
                    />
                  </div>
                  <div className="col-12 col-md-6 small text-muted">
                    CSV and JSON files from the documents library are shown here.
                  </div>
                </div>
              </div>
              <div className="table-responsive">
                <table className="table align-middle">
                  <thead>
                    <tr>
                      <th>File</th>
                      <th>Size</th>
                      <th>Uploaded</th>
                      <th>Import</th>
                      <th className="text-end">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(workflowFiles?.files || []).length > 0 ? (
                      (workflowFiles?.files || []).map((file) => (
                        <tr key={file.uuid}>
                          <td>{file.filename}</td>
                          <td>{formatFileSize(file.size_bytes) || <span className="text-muted">-</span>}</td>
                          <td>{formatUploadedAt(file.created_at) || <span className="text-muted">-</span>}</td>
                          <td>
                            {file.asset_import ? (
                              <span className={file.asset_import.active ? "text-primary" : "text-muted"}>
                                {file.asset_import.status}
                              </span>
                            ) : (
                              <span className="text-muted">-</span>
                            )}
                          </td>
                          <td className="text-end">
                            <button
                              type="button"
                              className="btn btn-sm btn-colored"
                              disabled={isWorking || !!file.asset_import?.active || (file.asset_import?.status === "ready" && !sessionCanContinue(file.asset_import as any))}
                              onClick={() => handleLibraryFile(file.uuid)}
                            >
                              {sessionCanContinue(file.asset_import as any) ? "Continue" : "Select"}
                            </button>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan={5} className="text-center text-muted py-5">
                          {isFetchingFiles ? "Loading files" : "No files"}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
              <MyPagination
                total={workflowFiles?.total || 0}
                defaultPerPage={10}
                perPageOptions={[10, 25, 50, 100]}
                forceRefresh={`${librarySearch}-${showLibrary}`}
                onPaginate={(page, perPage) => {
                  setLibraryPage(page);
                  setLibraryPerPage(perPage);
                }}
              />
            </div>
          </Modal.Body>
        </Modal>
      </>
    </MyModal>
  );
}
